// @ts-nocheck

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useSportsByGender } from '@/hooks/useSportsByGender';
import { usePositionsBySport } from '@/hooks/usePositionsBySport';
import { AlertTriangle } from 'lucide-react';
import {
  getStoredReferralCode,
  validateReferralCode,
  recordAffiliateEarning,
  clearReferralFromSession,
} from '@/utils/affiliateTracking';
import Cookies from 'js-cookie';
import { AthleteFormData } from '@/types/AthleteFormData';
import { states } from '@/lib/constants';
import { sendRegistrationFailureAlert } from '@/lib/registrationErrorAlert';
import { getSessionUser } from '@/lib/authSession';

const athleteSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email'),
  username: z.string().min(3, 'Username must be at least 3 characters'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  confirmPassword: z.string(),
  phone: z.string().regex(/^\d{3}-\d{3}-\d{4}$/, 'Phone must be in format xxx-xxx-xxxx'),
  city: z.string().min(2, 'City must be at least 2 characters'),
  state: z.string().min(2, 'State must be at least 2 characters'),
  gender: z.enum(['Men', 'Women'], {
    required_error: 'Please select a gender classification'
  }),
  sport_id: z.string().min(1, 'Please select a sport'),
  position_id: z.string().optional(),
  education_level: z.enum(['high_school', 'college'], {
    required_error: 'Please select education level'
  }),
  year: z.string().min(1, 'Please select a year'),
  agreeToTerms: z.boolean().refine(val => val === true, 'You must agree to the terms'),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

interface AthleteRegistrationFormProps {
  onSuccess?: () => void;
  variant?: 'basic' | 'premium';
  initialData?: Partial<AthleteFormData>;
}

const AthleteRegistrationForm: React.FC<AthleteRegistrationFormProps> = ({ 
  onSuccess, 
  variant = 'basic',
  initialData
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { refreshProfile, signUp } = useAuth();
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch
  } = useForm<AthleteFormData>({
    resolver: zodResolver(athleteSchema),
    defaultValues: initialData
  });

  const selectedGender = watch('gender');
  const selectedSportId = watch('sport_id');
  const selectedEducationLevel = watch('education_level');
  
  const { sports, isLoading: sportsLoading } = useSportsByGender(selectedGender);
  const { positions, isLoading: positionsLoading } = usePositionsBySport(selectedSportId);

  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    
    if (selectedEducationLevel === 'high_school') {
      // Show graduation years from current year to 2050
      const options = [];
      for (let year = currentYear; year <= 2050; year++) {
        options.push({ value: year.toString(), label: year.toString() });
      }
      return options;
    } else if (selectedEducationLevel === 'college') {
      return [
        { value: 'freshman', label: 'Freshman' },
        { value: 'sophomore', label: 'Sophomore' },
        { value: 'junior', label: 'Junior' },
        { value: 'senior', label: 'Senior' },
        { value: 'graduate', label: 'Graduate Student' },
      ];
    }
    return [];
  };

  const onSubmit = async (data: AthleteFormData) => {
    setIsLoading(true);

    try {
      // Step 1: Create the user account without email confirmation
      const signUpResult = await signUp(data.email, data.password, {
        name: data.name,
        username: data.username,
        role: 'athlete'
      });
      const authError = signUpResult.success ? null : { message: signUpResult.error };
      const { data: { user: signedUpUser } } = await getSessionUser();
      const authData = { user: signedUpUser } as any;

      if (authError) {
        throw authError;
      }

      if (!authData.user) {
        throw new Error('Failed to create user account');
      }

// Check for affiliate referral data
let affiliateId: string | null = null;
let commissionRate = 0;

const storedCode = getStoredReferralCode();
if (storedCode) {
  console.log('🔗 [REGISTRATION] Found stored referral code:', storedCode);
  const affiliateData = await validateReferralCode(storedCode);
  if (affiliateData) {
    affiliateId = affiliateData.affiliateId;
    commissionRate = Number(affiliateData.commissionRate) || 0;
    console.log('✅ [REGISTRATION] Affiliate validated:', affiliateId);
  }
}

      // Step 2: Create the profile with correct field mapping
      const profileData = {
        id: authData.user.id,       // profiles.id = auth user ID
        user_id: authData.user.id,  // profiles.user_id = auth user ID (same value)
        name: data.name,
        username: data.username,
        email: data.email,
        phone: data.phone,
        city: data.city,
        state: data.state,
        gender: data.gender,
        sport_id: data.sport_id,
        primary_position_id: data.position_id || null,
        education_level: data.education_level,
        // Map year field correctly based on education level
        college_year: selectedEducationLevel === 'college' ? data.year : null,
        grad_year: selectedEducationLevel === 'high_school' ? parseInt(data.year) : null,
        agree_terms: data.agreeToTerms,
        role: 'athlete' as const,
        primary_role_slug: 'athlete',
        membership_level: 'free',
        active_membership_slug: 'athlete_free',
        ...(affiliateId && { referred_by_affiliate_id: affiliateId })
      };

      console.log('📋 [REGISTRATION] Profile data being inserted:', profileData);

      // Use UPSERT since trigger may have already created basic profile
      const { data: profileResult, error: profileError } = await supabase
        .from('profiles')
        .upsert(profileData, { 
          onConflict: 'user_id',
          ignoreDuplicates: false 
        })
        .select()
        .single();

      if (profileError) {
        console.error('❌ [REGISTRATION] Profile creation failed:', profileError);
        throw profileError;
      }

      console.log('✅ [REGISTRATION] Profile created/updated successfully:', profileResult);

// Step 3: Track affiliate conversion via RPC
if (affiliateId) {
  try {
    const today = new Date().toISOString().slice(0, 10);
    await supabase.rpc('increment_affiliate_conversion', {
      p_affiliate_id: affiliateId,
      p_date: today
    });
    console.log('✅ [REGISTRATION] Affiliate conversion recorded via RPC');
  } catch (e) {
    console.warn('⚠️ [REGISTRATION] Affiliate conversion RPC failed:', e);
  }
}

// Step 4: Create default profile membership record using start_date
const { error: membershipError } = await supabase
  .from('profile_memberships')
  .insert({
    profile_id: profileResult.id,
    membership_slug: 'athlete_free',
    is_active: true,
    start_date: new Date().toISOString()
  });

if (membershipError) {
  console.warn('Profile membership creation warning:', membershipError);
}

      // Clear stored referral after successful registration
      try {
        clearReferralFromSession();
        localStorage.removeItem('affiliateReferralCode');
        localStorage.removeItem('affiliateReferralDate');
        sessionStorage.removeItem('affiliateReferralCode');
        sessionStorage.removeItem('affiliateReferralDate');
        Cookies.remove('affiliateReferralCode');
        console.log('🧹 [REGISTRATION] Referral cleared from storage');
      } catch (e) {
        console.warn('⚠️ [REGISTRATION] Failed clearing referral storage:', e);
      }

      // Step 4: Refresh auth context
      setTimeout(async () => {
        try {
          await refreshProfile();
        } catch (error) {
          console.warn('Profile refresh warning:', error);
        }
      }, 1000);

      toast.success('Account created successfully! Welcome to RecruitLook!');
      
      // Immediate redirect to athlete dashboard
      window.location.href = '/athlete-dashboard';

    } catch (error: any) {
      console.error('❌ [REGISTRATION] Registration failed:', error);
      
      // Send alert to admins about the failure
      await sendRegistrationFailureAlert({
        email: data.email,
        role: 'athlete',
        errorMessage: error.message || 'Unknown registration error',
        errorCode: error.code,
        firstName: data.name,
        step: 'registration',
        additionalContext: {
          sport_id: data.sport_id,
          education_level: data.education_level
        }
      });
      
      // Provide user-friendly error messages based on common database errors
      let errorMessage = 'Failed to create account. Please try again.';
      
      if (error.message) {
        if (error.message.includes('duplicate key') && error.message.includes('username')) {
          errorMessage = 'This username is already taken. Please choose a different username.';
        } else if (error.message.includes('duplicate key') && error.message.includes('email')) {
          errorMessage = 'An account with this email already exists. Please log in instead.';
        } else if (error.message.includes('foreign key') && error.message.includes('sport')) {
          errorMessage = 'Invalid sport selected. Please refresh the page and try again.';
        } else if (error.message.includes('foreign key') && error.message.includes('position')) {
          errorMessage = 'Invalid position selected. Please refresh the page and try again.';
        } else if (error.message.includes('violates check constraint')) {
          errorMessage = 'Invalid data provided. Please check all fields and try again.';
        } else if (error.code === '23505') {
          // Unique constraint violation
          errorMessage = 'An account with this username or email already exists.';
        } else if (error.code === '23503') {
          // Foreign key violation
          errorMessage = 'Invalid selection. Please refresh the page and try again.';
        } else {
          errorMessage = error.message;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle className="text-center">
          Create Athlete Account
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Alert variant="default" className="mb-6 border-yellow-200 bg-yellow-50">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-800">
            <strong>⚠️ NOTE:</strong> If you are a parent, please sign up as a parent and connect to the athlete through your dashboard. You will have full editing capabilities. Athletes should register themselves.
          </AlertDescription>
        </Alert>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* First row - Name and Email */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                type="text"
                placeholder="Enter your full name"
                {...register('name')}
                disabled={isLoading}
              />
              {errors.name && (
                <p className="text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                {...register('email')}
                disabled={isLoading}
              />
              {errors.email && (
                <p className="text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>
          </div>

          {/* Second row - Username and Phone */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="username">Username *</Label>
              <Input
                id="username"
                type="text"
                placeholder="Choose a username"
                {...register('username')}
                disabled={isLoading}
              />
              {errors.username && (
                <p className="text-sm text-red-600">{errors.username.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone *</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="xxx-xxx-xxxx"
                {...register('phone')}
                disabled={isLoading}
                maxLength={12}
                onChange={(e) => {
                  // Remove all non-digits
                  let value = e.target.value.replace(/\D/g, '');
                  
                  // Format with dashes as user types
                  if (value.length >= 6) {
                    value = value.slice(0, 3) + '-' + value.slice(3, 6) + '-' + value.slice(6, 10);
                  } else if (value.length >= 3) {
                    value = value.slice(0, 3) + '-' + value.slice(3, 6);
                  }
                  
                  setValue('phone', value, { shouldValidate: true });
                }}
              />
              {errors.phone && (
                <p className="text-sm text-red-600">{errors.phone.message}</p>
              )}
            </div>
          </div>

          {/* Third row - Password fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="password">Password *</Label>
              <Input
                id="password"
                type="password"
                placeholder="Create a password"
                {...register('password')}
                disabled={isLoading}
              />
              {errors.password && (
                <p className="text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password *</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Confirm your password"
                {...register('confirmPassword')}
                disabled={isLoading}
              />
              {errors.confirmPassword && (
                <p className="text-sm text-red-600">{errors.confirmPassword.message}</p>
              )}
            </div>
          </div>

          {/* Fourth row - City and State */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city">City *</Label>
              <Input
                id="city"
                type="text"
                placeholder="Enter your city"
                {...register('city')}
                disabled={isLoading}
              />
              {errors.city && (
                <p className="text-sm text-red-600">{errors.city.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="state">State *</Label>
              <Select onValueChange={(value) => setValue('state', value)}>
                <SelectTrigger disabled={isLoading}>
                  <SelectValue placeholder="Select your state" />
                </SelectTrigger>
                <SelectContent>
                  {states.map((state) => (
                    <SelectItem key={state.value} value={state.value}>
                      {state.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.state && (
                <p className="text-sm text-red-600">{errors.state.message}</p>
              )}
            </div>
          </div>

          {/* Fifth row - Gender and Sport */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="gender">NCAA Gender Classification *</Label>
              <Select onValueChange={(value) => setValue('gender', value as 'Men' | 'Women')}>
                <SelectTrigger disabled={isLoading}>
                  <SelectValue placeholder="Select gender classification" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Men">Men</SelectItem>
                  <SelectItem value="Women">Women</SelectItem>
                </SelectContent>
              </Select>
              {errors.gender && (
                <p className="text-sm text-red-600">{errors.gender.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="sport_id">Sport *</Label>
              <Select onValueChange={(value) => setValue('sport_id', value)} disabled={!selectedGender || sportsLoading}>
                <SelectTrigger disabled={isLoading || !selectedGender || sportsLoading}>
                  <SelectValue placeholder={
                    !selectedGender ? "Select gender first" : 
                    sportsLoading ? "Loading sports..." : 
                    "Select your sport"
                  } />
                </SelectTrigger>
                <SelectContent>
                  {sports.map((sport) => (
                    <SelectItem key={sport.id} value={sport.id}>
                      {sport.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.sport_id && (
                <p className="text-sm text-red-600">{errors.sport_id.message}</p>
              )}
            </div>
          </div>

          {/* Sixth row - Position */}
          {selectedSportId && (
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label htmlFor="position_id">Position</Label>
                <Select onValueChange={(value) => setValue('position_id', value)} disabled={!selectedSportId || positionsLoading}>
                  <SelectTrigger disabled={isLoading || !selectedSportId || positionsLoading}>
                    <SelectValue placeholder={
                      !selectedSportId ? "Select sport first" : 
                      positionsLoading ? "Loading positions..." : 
                      "Select your position (optional)"
                    } />
                  </SelectTrigger>
                  <SelectContent>
                    {positions.map((position) => (
                      <SelectItem key={position.id} value={position.id}>
                        {position.position_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.position_id && (
                  <p className="text-sm text-red-600">{errors.position_id.message}</p>
                )}
              </div>
            </div>
          )}

          {/* Seventh row - Education Level and Year */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="education_level">Education Level *</Label>
              <Select onValueChange={(value) => setValue('education_level', value as 'high_school' | 'college')}>
                <SelectTrigger disabled={isLoading}>
                  <SelectValue placeholder="Select education level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="high_school">High School</SelectItem>
                  <SelectItem value="college">College</SelectItem>
                </SelectContent>
              </Select>
              {errors.education_level && (
                <p className="text-sm text-red-600">{errors.education_level.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="year">
                {selectedEducationLevel === 'high_school' ? 'Graduation Year *' : 'Year *'}
              </Label>
              <Select onValueChange={(value) => setValue('year', value)} disabled={!selectedEducationLevel}>
                <SelectTrigger disabled={isLoading || !selectedEducationLevel}>
                  <SelectValue placeholder={
                    !selectedEducationLevel ? "Select education level first" : 
                    selectedEducationLevel === 'high_school' ? "Select graduation year" : "Select your year"
                  } />
                </SelectTrigger>
                <SelectContent>
                  {getYearOptions().map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.year && (
                <p className="text-sm text-red-600">{errors.year.message}</p>
              )}
            </div>
          </div>

          {/* Terms checkbox */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="agreeToTerms"
              onCheckedChange={(checked) => setValue('agreeToTerms', !!checked)}
              disabled={isLoading}
            />
            <Label htmlFor="agreeToTerms" className="text-sm">
              I agree to the Terms of Service and Privacy Policy *
            </Label>
          </div>
          {errors.agreeToTerms && (
            <p className="text-sm text-red-600">{errors.agreeToTerms.message}</p>
          )}

          <Button 
            type="submit" 
            className="w-full h-12 bg-secondary hover:bg-secondary/90 text-slate-900 font-black text-lg" 
            disabled={isLoading}
          >
            {isLoading ? 'Creating Account...' : 'CREATE ACCOUNT'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default AthleteRegistrationForm;